import React, { useEffect, useState } from "react";
import { Formik, Field, FieldArray, Form, getIn } from "formik";
import {
  VStack,
  HStack,
  Text,
  Box,
  Select,
  Button,
  Spinner,
  Flex,
  NumberInput,
  NumberInputField,
} from "@chakra-ui/react";
import { StepProps } from "../../../../../../../utils/types/step";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../../../redux/reducer/rootReducer";
import * as Yup from "yup";
import StepHeader from "../../../../../../../components/StepperHeader";
import { ArrowLeftIcon, ArrowRightIcon } from "@chakra-ui/icons";
import { saveFormData } from "../../../../../../../redux/slice/formSlice";

const oneToFiveOptions = [
  { label: "1", value: 1 },
  { label: "2", value: 2 },
  { label: "3", value: 3 },
  { label: "4", value: 4 },
  { label: "5", value: 5 },
];

export interface ProblemRanking {
  name: string;
  frequency: string;
  severity: string;
  score: string;
}
export interface Ranking {
  secondaryCauses: ProblemRanking[];
}

const Prioritize = (props: StepProps) => {
  const priotizeData = useSelector(
    (state: RootState) => state.form.formData["5"]
  );
  const handleSubmit = (values: any) => {
    let data = values.secondaryCauses.map((value: ProblemRanking) => {
      return {
        name: value.name,
        frequency: value.frequency,
        severity: value.severity,
        score: parseInt(value.frequency) + parseInt(value.severity),
      };
    });

    console.log("prioritize data: ", data);

    dispatch(saveFormData({ step: "5", data: data }));

    props.onNext();
  };

  const [initialValues, setInitialValues] = useState<Ranking>({
    secondaryCauses: [],
  });

  const dispatch = useDispatch();
  const fisboneData = useSelector(
    (state: RootState) => state.form.formData["4"]
  );

  const schema = Yup.object().shape({
    secondaryCauses: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required("Required"),
        frequency: Yup.number()
          .typeError("Frequency must be a number")
          .required("Required"),
        severity: Yup.number()
          .typeError("Severity must be a number")
          .required("Required"),
      })
    ),
  });

  useEffect(() => {
    let mappedSecondaryCauses: ProblemRanking[] = [];

    // Check if prioritizeData has entries
    if (priotizeData && Object.values(priotizeData).length > 0) {
      console.log("prioritize data: ", priotizeData);

      // Map prioritizeData into ProblemRanking format
      mappedSecondaryCauses = Object.values(priotizeData).map((cause: any) => ({
        name: cause.name,
        frequency: cause.frequency,
        severity: cause.severity,
        score: cause.score,
      }));

      // Add new data from fishboneData that's not already in prioritizeData
      const existingNames = new Set(
        mappedSecondaryCauses.map((cause) => cause.name)
      );

      const newSecondaryCauses = fisboneData.causes.flatMap((cause: any) =>
        cause.primaryCauses.flatMap((primary: any) =>
          primary.secondaryCauses
            .filter((secondary: any) => !existingNames.has(secondary)) // Check for new data
            .map((secondary: any) => ({
              name: secondary || "",
              frequency: "",
              severity: "",
              score: "",
            }))
        )
      );

      // Combine the mapped prioritizeData with the new secondary causes
      mappedSecondaryCauses = [...mappedSecondaryCauses, ...newSecondaryCauses];
    } else {
      // Fallback when prioritizeData is empty
      mappedSecondaryCauses = fisboneData.causes.flatMap((cause: any) =>
        cause.primaryCauses.flatMap((primary: any) =>
          primary.secondaryCauses.map((secondary: any) => ({
            name: secondary || "",
            frequency: "",
            severity: "",
            score: "",
          }))
        )
      );
    }

    console.log("mapped secondary causes: ", mappedSecondaryCauses);

    // Update initial values with the final mapped secondary causes
    const updatedInitialValues = {
      secondaryCauses: [...mappedSecondaryCauses],
    };

    setInitialValues(updatedInitialValues);
  }, [priotizeData, fisboneData]); // Add dependencies for reactivity

  useEffect(() => {
    console.log(initialValues);
  }, [initialValues]);

  return (
    <>
      {initialValues?.secondaryCauses.length === 0 ? (
        <Spinner />
      ) : (
        <>
          <StepHeader
            title="Prioritize"
            description="Now that we have listed possible root causes for our Problem, we need to prioritize them based on their frequency and severity. By focusing on the most significant issues, we can allocate resources effectively and implement changes that will have the greatest outcome.
"
          />
          <HStack
            display="flex"
            flexDirection="row"
            w="full"
            gap={10}
            px={5}
            borderRadius={5}
            my={4}
          >
            <Text flex={0.1} textAlign={"left"}>
              #
            </Text>
            <Text flex={0.7} textAlign={"left"}>
              Problems
            </Text>

            <Text flex={0.4} textAlign={"left"}>
              Frequency
            </Text>

            <Text flex={0.4} textAlign={"left"}>
              Severity
            </Text>

            <Text flex={0.3} textAlign={"center"}>
              Total Score
            </Text>
          </HStack>
          <Formik
            initialValues={initialValues}
            validateOnBlur={true}
            validationSchema={schema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, isValid, handleChange, touched }) => {
              console.log("is form valid: ", isValid);

              return (
                <Form>
                  <FieldArray
                    name="secondaryCauses"
                    render={({ remove, push }) => (
                      <>
                        {values.secondaryCauses.map(
                          (secondaryCause: ProblemRanking, index: number) => (
                            <React.Fragment key={index}>
                              <HStack
                                key={`${index}-${secondaryCause}`}
                                display="flex"
                                flexDirection="row"
                                w="full"
                                p={4}
                                gap={10}
                                borderColor="gray.100"
                                borderWidth={1}
                                borderRadius={5}
                                m={1}
                              >
                                <Flex flex={0.1} direction={"row"} gap={2}>
                                  <Text>{index + 1}</Text>
                                </Flex>
                                <Flex flex={0.7} direction={"row"} gap={2}>
                                  <Text textAlign={"left"}>
                                    {secondaryCause.name}
                                  </Text>
                                </Flex>

                                <VStack flex={0.4}>
                                  <Field
                                    as={Select}
                                    style={{
                                      border: getIn(
                                        errors,
                                        `secondaryCauses[${index}].frequency`
                                      )
                                        ? "1px solid red"
                                        : "1px solid #E2E8F0",
                                    }}
                                    name={`secondaryCauses[${index}].frequency`}
                                  >
                                    <option value={""}>Select Frequency</option>
                                    {oneToFiveOptions.map((option, index) => (
                                      <option key={index} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </Field>
                                </VStack>
                                <VStack flex={0.4}>
                                  <Field
                                    as={Select}
                                    style={{
                                      border: getIn(
                                        errors,
                                        `secondaryCauses[${index}].severity`
                                      )
                                        ? "1px solid red"
                                        : "1px solid #E2E8F0",
                                    }}
                                    name={`secondaryCauses[${index}].severity`}
                                  >
                                    <option value={""}>Select Severity</option>
                                    {oneToFiveOptions.map((option, index) => (
                                      <option key={index} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </Field>
                                </VStack>

                                <VStack
                                  flex={0.3}
                                  direction={"row"}
                                  gap={2}
                                  alignContent={"center"}
                                  alignItems={"center"}
                                  justifyContent={"center"}
                                  backgroundColor={"green.100"}
                                  padding={1}
                                >
                                  <Text
                                    textAlign={"center"}
                                    fontSize={"large"}
                                    fontWeight={"bold"}
                                  >
                                    {!isNaN(
                                      parseInt(secondaryCause.frequency)
                                    ) &&
                                    !isNaN(parseInt(secondaryCause.severity))
                                      ? parseInt(secondaryCause.frequency) +
                                        parseInt(secondaryCause.severity)
                                      : 0}
                                  </Text>
                                </VStack>
                              </HStack>
                            </React.Fragment>
                          )
                        )}
                      </>
                    )}
                  />
                  <HStack
                    flex={0.1}
                    w="100%"
                    mt={5}
                    justifyContent="space-between"
                    p={4}
                    bg="gray.50"
                  >
                    <Button
                      colorScheme="blue"
                      variant="outline"
                      leftIcon={<ArrowLeftIcon />}
                      onClick={() => {
                        if (props.onPrev) {
                          props.onPrev();
                        }
                      }}
                    >
                      Previous
                    </Button>
                    <HStack>
                      <Button
                        isDisabled={!isValid || !touched}
                        colorScheme="blue"
                        variant="outline"
                      >
                        Save & Exit
                      </Button>
                      <Button
                        colorScheme="blue"
                        isDisabled={
                          !isValid ||
                          !Object.keys(touched).length || // Check if the form has been touched
                          !Object.values(values).some(
                            (value) =>
                              Array.isArray(value) ? value.length > 0 : !!value // Check if there's any entered data
                          )
                        }
                        type="submit"
                        w={"50%"}
                        rightIcon={<ArrowRightIcon />}
                      >
                        Next
                      </Button>
                    </HStack>
                  </HStack>
                </Form>
              );
            }}
          </Formik>
        </>
      )}
    </>
  );
};

export default Prioritize;
